import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { UserContext } from '../../context';
import {
  leaderBoardApi,
  useLeaderBoardBadgesQuery,
  useLeaderBoardUsersQuery,
} from '../../services';
import { getCssVar, IconLabel, isAdminLayout, LastDateValue, useLastDate } from '../../shared';
import { DateFilter } from './DateFilter';
import { Description } from './Description';
import { renderPositionCell, renderPositionDiffCell, renderUserCell } from './helpers';
import { SelfStats } from './SelfStats';

import classNames from 'classnames';
import classes from './LeaderBoard.module.scss';

export const LeaderBoard: FunctionComponent = () => {
  const { t } = useTranslation();

  const { data: leaderBoardBages = [], isFetching: isFetchingBadges } = useLeaderBoardBadgesQuery();

  const renderRank = useMemo(() => Boolean(leaderBoardBages.length), [leaderBoardBages.length]);

  const rankAsColumn = useMemo(() => renderRank && !isMobileOnly, [renderRank]);

  const rankWithName = useMemo(() => renderRank && isMobileOnly, [renderRank]);

  const [generateReport] = leaderBoardApi.endpoints.leaderBoardGenerateCSVReport.useMutation();

  const { getLastDate } = useLastDate();

  const { id: userId } = useContext(UserContext).userProfile;

  const [dateFilterValue, setDateFilterValue] = useState<LastDateValue>(LastDateValue.LAST_30_DAYS);

  const [page, setPage] = useState<number>(1);

  const { from, to } = useMemo(() => getLastDate(dateFilterValue), [dateFilterValue, getLastDate]);

  const filters = useMemo(() => {
    return { from: from ?? '', to: to ?? '' };
  }, [from, to]);

  const { data: leaderBoardData, isFetching: isFetchingLeaderBoard } = useLeaderBoardUsersQuery({
    filters,
    page,
  });

  const setPageHandler = useCallback((page: number) => {
    setPage(page);
    window.scrollTo(0, 0);
  }, []);

  const renderPagination = useMemo(() => {
    if (!leaderBoardData) {
      return null;
    }

    const { pageInfo } = leaderBoardData;

    const { page, hasNextPage, hasPreviousPage, totalPages } = pageInfo;

    if (totalPages <= 1) {
      return null;
    }

    const maxPagesToShow = 10;

    let pages: (number | '...')[] = [];

    if (totalPages <= maxPagesToShow) {
      pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      pages.push(1);

      const middlePagesToShow = maxPagesToShow - 2;

      let startPage = Math.max(2, page - Math.floor(middlePagesToShow / 2));

      const endPage = Math.min(totalPages - 1, startPage + middlePagesToShow - 1);

      if (endPage === totalPages - 1) {
        startPage = Math.max(2, endPage - middlePagesToShow + 1);
      }

      if (startPage > 2) {
        pages.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return (
      <div className={classes['leader-board__pagination']}>
        <>
          <IconLabel
            iconSize={20}
            iconId={'arrow-left'}
            disabled={!hasPreviousPage}
            onClick={() => setPageHandler(pageInfo.page - 1)}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
          />
          {pages.map((indexOrDots, index) => {
            const isPageIndex = Number(indexOrDots);

            return (
              <div
                key={index}
                className={classNames(classes['leader-board__pagination-page'], {
                  [classes['leader-board__pagination-page--clickable']]: isPageIndex,
                  [classes['leader-board__pagination-page--active']]: page === indexOrDots,
                })}
                {...(isPageIndex && { onClick: () => setPageHandler(indexOrDots as number) })}
              >
                {indexOrDots}
              </div>
            );
          })}
          <IconLabel
            iconSize={20}
            iconId={'arrow-right'}
            disabled={!hasNextPage}
            onClick={() => setPageHandler(pageInfo.page + 1)}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
          />
        </>
      </div>
    );
  }, [leaderBoardData, setPageHandler]);

  const generateReportHandler = useCallback(async () => {
    const sent = await generateReport({ ...filters }).unwrap();
    toast(t(`common.${sent ? 'report-sent' : 'error-message'}`));
  }, [filters, generateReport, t]);

  if (isFetchingBadges || (isFetchingLeaderBoard && !leaderBoardData)) {
    return <Skeleton height={'10rem'} />;
  }

  if (!leaderBoardData) {
    return null;
  }

  const { items } = leaderBoardData;

  if (!items.length) {
    return null;
  }

  return (
    <div
      className={classNames(classes['leader-board'], {
        [classes['leader-board--mobile']]: isMobileOnly,
      })}
    >
      <div className={classes['leader-board__title']}>{t('leaderBoard.schlager-title')}</div>

      <div
        className={classNames(classes['leader-board__header'], {
          [classes['leader-board__header--mobile']]: isMobileOnly,
        })}
      >
        <Description />

        <div
          className={classNames(classes['leader-board__header-wrapper'], {
            [classes['leader-board__header-wrapper--mobile']]: isMobileOnly,
          })}
        >
          <DateFilter value={dateFilterValue} setValue={setDateFilterValue} />

          {isAdminLayout() && (
            <IconLabel
              iconId={'donwload'}
              color={'#292E4B'}
              onClick={generateReportHandler}
              singleColor
            />
          )}
        </div>
      </div>

      <SelfStats leaderBoardBages={leaderBoardBages} />

      <table className={classes['leader-board__table']}>
        <colgroup>
          <col width={'40'} />
          <col width={isMobileOnly ? '170' : '300'} />
          <col width={isMobileOnly ? '60' : '140'} />
          {rankAsColumn && <col width={'180'} />}
          <col width={isMobileOnly ? '70' : '80'} />
        </colgroup>
        <thead className={classes['leader-board__table-thead']}>
          <tr>
            <th className={classes['leader-board__table-position']}>#</th>
            <th>{t('leaderBoardTableColumns.name')}</th>
            <th>{t('leaderBoardTableColumns.points')}</th>
            {rankAsColumn && <th>{t('leaderBoardTableColumns.rank')}</th>}
            <th>{t('leaderBoardTableColumns.position')}</th>
          </tr>
        </thead>
        <tbody
          className={classNames(classes['leader-board__table-tbody'], {
            [classes['leader-board__table-tbody--loading']]: isFetchingLeaderBoard,
          })}
        >
          {items.map(({ user, position, totalPoints, positionDiff }) => {
            const { id, leaderBoardBadge } = user;
            const rank = t(`leaderBoardBadges.${leaderBoardBadge?.name}`);

            return (
              <tr key={id}>
                {renderPositionCell({ position, isActive: id === userId })}
                {renderUserCell({ user, ...(rankWithName && { rank }) })}
                <td>{totalPoints}</td>
                {rankAsColumn && <td>{rank}</td>}
                <td>{renderPositionDiffCell(positionDiff)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {renderPagination}
    </div>
  );
};
